import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import { Badge, Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import ChakraReactTable from "components/Shared/ChakraReactTable";
import {
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import TableControlsFooter from "components/Shared/ChakraReactTable/TableControlsFooter";
import NumResultsLabel from "components/Shared/ChakraReactTable/NumResultsLabel";
import TableControls from "components/Shared/ChakraReactTable/TableControls";
import PaginationControls from "components/Shared/ChakraReactTable/PaginationControls";
import { formatDateTimeCell } from "components/Shared/ChakraReactTable/formatters";
import { CONTENT_FULL_HEIGHT } from "components/Layout/constants";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import ScrollContainer from "components/Shared/ChakraReactTable/ScrollContainer";
import { BrandUserFilter, BrandUsersOrderBy } from "__generated__/gql/graphql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import { useCurrentBrandId } from "utils/currentUserHooks";
import UserChip from "components/Shared/UserChip";
import { useOptionalCombineFilters } from "utils/useCombineFilters";
import ResendInviteButton from "components/Core/AccountInfo/Users/ResendInviteButton";
import RemoveBrandUserButton from "components/Core/AccountInfo/Users/RemoveBrandUserButton";

const fragment = gql(/* GraphQL */ `
  fragment BrandUsersTableDatum on BrandUser {
    id
    createdAt
    user {
      id
      email
      fullName
      inviteAcceptedAt
      isActive
      lastLogin
      ...UserChipComponent
    }
    ...RemoveBrandUserButton
  }
`);

const query = gql(/* GraphQL */ `
  query BrandUsersTable(
    $brandId: UUID!
    $filter: BrandUserFilter
    $orderBy: [BrandUsersOrderBy!] = [CREATED_AT_ASC]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    brandUsers(
      condition: { brandId: $brandId }
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      nodes {
        id
        ...BrandUsersTableDatum
      }
    }
  }
`);

export type Datum = DocumentType<typeof fragment>;

const col = createColumnHelper<Datum>();

export const DEFAULT_COLUMNS = [
  col.accessor((d) => d.user?.fullName, {
    id: "user",
    header: "User",
    cell: (cell) => <UserChip user={cell.row.original.user} />,
    size: 200,
  }),
  col.accessor("createdAt", {
    header: "Added",
    cell: formatDateTimeCell,
    size: 135,
    meta: { props: { textAlign: "center" }, cellProps: { fontSize: "xs" } },
  }),
  col.display({
    id: "status",
    header: "Status",
    cell: (cell) => {
      const brandUser = cell.row.original;

      if (!brandUser.user) return null;

      const { inviteAcceptedAt, isActive, lastLogin } = brandUser.user;

      if (!isActive) return <Badge>Inactive</Badge>;

      const isInvitePending = !inviteAcceptedAt && !lastLogin;

      return (
        <>
          <Badge colorScheme={isInvitePending ? undefined : "green"}>
            {isInvitePending ? "Invited" : "Active"}
          </Badge>
          {isInvitePending && <ResendInviteButton brandUserId={brandUser.id} />}
        </>
      );
    },
    size: 115,
    meta: { props: { textAlign: "center", pr: 1 } },
  }),
  col.display({
    id: "actions",
    cell: (cell) => {
      const brandUser = cell.row.original;
      return <RemoveBrandUserButton brandUser={brandUser} />;
    },
    size: 50,
    meta: { props: { textAlign: "right", pl: 1 } },
  }),
];

export const DEFAULT_PINNED_COLUMNS: ColumnPinningState = {
  left: ["user"],
};

const orderByEnumsById: GQLOrderByEnumsById<BrandUsersOrderBy> = {
  user: [
    BrandUsersOrderBy.USER__LAST_NAME_ASC,
    BrandUsersOrderBy.USER__LAST_NAME_DESC,
  ],
  createdAt: [
    BrandUsersOrderBy.CREATED_AT_ASC,
    BrandUsersOrderBy.CREATED_AT_DESC,
  ],
};

export type BrandUsersTableProps = Partial<
  Pick<TableOptions<Datum>, "columns" | "meta">
> & {
  filters?: BrandUserFilter[];
  pinnedColumns?: ColumnPinningState;
};

export default function BrandUsersTable({
  columns = DEFAULT_COLUMNS,
  filters,
  meta,
  pinnedColumns: columnPinning = DEFAULT_PINNED_COLUMNS,
}: BrandUsersTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination();

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "createdAt", desc: false }],
  );

  const brandId = useCurrentBrandId();
  const filter = useOptionalCombineFilters(filters);

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading,
    refetch,
  } = useBrandQuery(query, {
    skip: !brandId || isPendingFilterReset || isPendingSortingReset,
    variables: brandId
      ? { brandId, filter, orderBy, ...limitOffset }
      : undefined,
  });

  const { nodes, totalCount } = rawData?.brandUsers || {};

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(getFragmentData(fragment, nodes)),
    meta,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useChakraReactTableProps<Datum>([
    useStickyHeader(),
    useFixedLayout(tableInstance),
    {
      tableProps: { fontSize: "sm" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
  ]);

  const isLoading = loading || typeof window === "undefined";

  return (
    <>
      <ScrollContainer
        minHeight="15rem"
        maxHeight={`calc(${CONTENT_FULL_HEIGHT} - 15.5rem)`}
        borderWidth={1}
        borderTopRadius=".25rem"
      >
        <ChakraReactTable
          tableInstance={tableInstance}
          {...tableProps}
          isLoading={isLoading}
          noRowsPlaceholder={<Text>No users found.</Text>}
        />
      </ScrollContainer>
      <TableControlsFooter
        py={4}
        borderTopWidth={1}
        leftContent={
          <NumResultsLabel
            {...{ totalCount, isLoading }}
            resultTypeLabel="users"
          />
        }
        rightContent={<TableControls {...{ isLoading }} onRefresh={refetch} />}
      >
        <PaginationControls {...{ tableInstance, isLoading }} />
      </TableControlsFooter>
    </>
  );
}
